<template>
  <div class="policy-container">
    <h1>Child Sexual Abuse and Exploitation (CSAE) Policy</h1>
    <p><strong>Effective Date:</strong> 17 October 2025</p>

    <p>
      At <strong>Meetch</strong>, we are committed to ensuring our platform remains safe, secure,
      and completely free from any content related to Child Sexual Abuse and Exploitation (CSAE).
      This policy outlines our zero-tolerance approach and the measures we take to prevent,
      report, and act against such content in compliance with
      <strong>Google Play’s Child Safety Standards Policy</strong> and all applicable laws.
    </p>

    <section>
      <h2>1. Prohibited Content</h2>
      <p>
        Meetch strictly prohibits any form of CSAE, including but not limited to:
      </p>
      <ul>
        <li>Images, videos, or written descriptions of child sexual exploitation or abuse.</li>
        <li>Any content that promotes, facilitates, or solicits child sexual abuse.</li>
        <li>Attempts to share, distribute, or link to CSAE content in any format.</li>
      </ul>
      <p>
        Violation of this policy will result in immediate removal of content, account suspension,
        and reporting to law enforcement authorities as required by law.
      </p>
    </section>

    <section>
      <h2>2. Preventative Measures</h2>
      <p>
        To protect users and prevent CSAE content from appearing on our platform,
        Meetch implements the following safeguards:
      </p>
      <ul>
        <li>
          <strong>Strict Content Moderation:</strong>
          Automated and manual moderation systems detect and remove prohibited content.
        </li>
        <li>
          <strong>User Reporting Mechanism:</strong>
          Users can report any suspected CSAE content directly within the app.
        </li>
        <li>
          <strong>Account Monitoring & Verification:</strong>
          User verification processes reduce the risk of exploitation.
        </li>
        <li>
          <strong>Regular Policy Reviews:</strong>
          Our CSAE policy and enforcement procedures are regularly updated to
          ensure compliance with current laws and best practices.
        </li>
      </ul>
    </section>

    <section>
      <h2>3. Reporting CSAE</h2>
      <p>
        If any CSAE content is identified on our platform, Meetch will:
      </p>
      <ul>
        <li>Immediately remove the content.</li>
        <li>Suspend or permanently ban the responsible user account.</li>
        <li>
          Report confirmed cases to the appropriate law enforcement authorities, such as:
          <ul>
            <li>The National Center for Missing &amp; Exploited Children (NCMEC) – U.S.</li>
            <li>The Internet Watch Foundation (IWF) – U.K.</li>
            <li>Local law enforcement agencies where applicable.</li>
          </ul>
        </li>
        <li>Cooperate fully with investigations by providing necessary evidence.</li>
      </ul>
    </section>

    <section>
      <h2>4. How to Report</h2>
      <p>
        Users can report any suspected CSAE content via the support pages on our website.
        Reports can also be sent to <a href="mailto:info@meetch.app">info@meetch.app</a> for immediate action.
      </p>
    </section>

    <section>
      <h2>5. Compliance with Laws &amp; Regulations</h2>
      <p>
        Meetch complies with all applicable child safety laws and regulations,
        including but not limited to:
      </p>
      <ul>
        <li>The UK’s Protection of Children Act 1978</li>
        <li>The Children’s Online Privacy Protection Act (COPPA) – U.S.</li>
        <li>The European General Data Protection Regulation (GDPR) – Child Safety Provisions</li>
        <li>International standards for child protection and online safety.</li>
      </ul>
    </section>

    <section>
      <h2>6. Child Safety Contact Point</h2>
      <p>
        To ensure a direct line of communication for child safety concerns, Meetch has designated a
        <strong>Child Safety Contact Representative</strong> to:
      </p>
      <ul>
        <li>Receive and respond to notifications regarding CSAE content
          from Google Play and other platforms.
        </li>
        <li>Work directly with law enforcement agencies.</li>
        <li>Ensure that our reporting and removal mechanisms remain effective.</li>
      </ul>
      <p>
        For urgent concerns, please contact our Child Safety Representative at
        <a href="mailto:info@meetch.app">info@meetch.app</a>.
      </p>
    </section>

    <section>
      <h2>7. Enforcement &amp; Consequences</h2>
      <p>
        Users found violating this policy will face immediate and permanent account suspension.
        Legal action will be pursued where applicable. We reserve the right to take any additional
        measures necessary to ensure full compliance with this policy.
      </p>
    </section>

    <section>
      <h2>8. Policy Updates</h2>
      <p>
        This policy may be updated periodically to reflect changes in laws,
        regulations, and best practices.
        We encourage users to review it regularly.
      </p>
      <p>
        For any questions or concerns about this policy, please contact us
        via the support pages on our website
        or by emailing <a href="mailto:info@meetch.app">info@meetch.app</a>.
      </p>
    </section>
  </div>
</template>

<script setup>
// No dynamic logic required — static informational page.
</script>

<style scoped>
.policy-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: 'Inter', sans-serif;
  line-height: 1.6;
  text-align: left;
  color: #222;
}

h1 {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

h2 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
}

ul {
  list-style: disc;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

a {
  color: #0066cc;
  text-decoration: underline;
}

@media (max-width: 600px) {
  .policy-container {
    padding: 1.5rem 1rem;
  }
}
</style>
